import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GitCompare, LibraryBig } from "lucide-react";
import api from "../../lib/api";
import { fmtDate } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { EmptyState, PageHeader, ToneChip } from "../../components/common";
import { Button } from "../../components/ui/button";

export default function Modelos() {
  const navigate = useNavigate();
  const { map } = useUsers();
  const { data: templates, isLoading } = useQuery({
    queryKey: ["contract-templates"],
    queryFn: async () => (await api.get("/legal/templates")).data,
  });
  const { data: precedents } = useQuery({
    queryKey: ["precedents"],
    queryFn: async () => (await api.get("/legal/contracts", { params: { status: "activo" } })).data,
  });

  return (
    <div data-testid="modelos-page">
      <PageHeader title="Modelos y precedentes" subtitle="Modelos corporativos aprobados y contratos activos utilizables como precedente en comparaciones." />
      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando…</p>
      ) : (
        <>
          <h2 className="ts-section-title mb-3">Modelos corporativos</h2>
          {!templates?.length ? <EmptyState title="Sin modelos" /> : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {templates.map((t) => (
                <div key={t.id} className="ts-card p-4" data-testid={`template-${t.id}`}>
                  <div className="flex items-start gap-2">
                    <LibraryBig className="mt-0.5 h-4 w-4 text-blue-700" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900">{t.name}</p>
                      <p className="mt-0.5 text-xs text-slate-500">v{t.version} · {t.status} · propietario {userName(map, t.owner_id)} · actualizado {fmtDate(t.updated_at)}</p>
                      <p className="mt-1 text-xs text-slate-600">{t.description}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate("/legal/comparaciones")} data-testid={`template-compare-${t.id}`}>
                    <GitCompare className="mr-1 h-3.5 w-3.5" /> Usar en comparación
                  </Button>
                </div>
              ))}
            </div>
          )}
          <h2 className="ts-section-title mb-3 mt-8">Precedentes (contratos activos)</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {(precedents || []).slice(0, 12).map((c) => (
              <button key={c.id} onClick={() => navigate(`/legal/contratos/${c.id}`)} className="ts-card ts-card-hover p-4 text-left" data-testid={`precedent-${c.id}`}>
                <p className="truncate text-sm font-medium text-slate-900">{c.title}</p>
                <p className="mt-0.5 font-mono text-xs text-slate-400">{c.contract_number}</p>
                <div className="mt-2 flex gap-1.5">
                  <ToneChip label={c.contract_type} tone="muted" />
                  <ToneChip label={`${(c.total_value / 1000).toFixed(0)} k€`} tone="info" />
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
